import { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, TextField, IconButton, Tooltip, Chip, FormControlLabel, Switch } from '@mui/material';
import { Add, Visibility, Edit, Delete, Business, Person, RestoreFromTrash, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import AppLayout from '../../components/layout/AppLayout';
import DataTable from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import clientiService from '../../services/clientiService';
import type { Cliente } from '../../types/cliente.types';
import type { TableColumn } from '../../types/common.types';
import { getErrorMessage } from '../../utils/api';

const ClientiListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null
  });
  const [hardDeleteDialog, setHardDeleteDialog] = useState<{ open: boolean; id: string | null; nome: string }>({
    open: false,
    id: null,
    nome: ''
  });
  const [showDeleted, setShowDeleted] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['clienti', page, rowsPerPage, debouncedSearch, showDeleted],
    queryFn: () =>
      clientiService.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch,
        includeDeleted: showDeleted
      })
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => clientiService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clienti'] });
      setDeleteDialog({ open: false, id: null });
      enqueueSnackbar('Cliente eliminato con successo', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante l\'eliminazione'), { variant: 'error' });
    }
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => clientiService.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clienti'] });
      enqueueSnackbar('Cliente ripristinato con successo', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante il ripristino'), { variant: 'error' });
    }
  });

  const hardDeleteMutation = useMutation({
    mutationFn: (id: string) => clientiService.hardDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clienti'] });
      setHardDeleteDialog({ open: false, id: null, nome: '' });
      enqueueSnackbar('Cliente eliminato definitivamente', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante l\'eliminazione definitiva'), { variant: 'error' });
    }
  });

  const columns: TableColumn<Cliente>[] = [
    {
      id: 'cognome',
      label: 'Cognome / Nome',
      minWidth: 150,
      format: (_, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {row.personaGiuridica ? (
            <Business fontSize="small" color="primary" />
          ) : (
            <Person fontSize="small" color="action" />
          )}
          {`${row.cognome} ${row.nome}`.trim()}
        </Box>
      )
    },
    { id: 'codiceFiscale', label: 'Codice Fiscale', minWidth: 130 },
    { id: 'email', label: 'Email', minWidth: 200 },
    { 
      id: 'pec', 
      label: 'PEC', 
      minWidth: 200,
      format: (value: unknown) => (value as string) || '-'
    },
    { id: 'telefono', label: 'Telefono', minWidth: 120 },
    {
      id: 'citta',
      label: 'Citta',
      minWidth: 120,
      format: (_, row) => (row.citta && row.provincia ? `${row.citta} (${row.provincia})` : row.citta || '-')
    },
    {
      id: 'personaGiuridica',
      label: 'Tipo',
      minWidth: 100,
      format: (value) => (
        <Chip
          label={value ? 'P. Giuridica' : 'P. Fisica'}
          color={value ? 'primary' : 'default'}
          variant={value ? 'filled' : 'outlined'}
          size="small"
          icon={value ? <Business /> : <Person />}
        />
      )
    }
  ];

  // Stile per le righe eliminate (soft deleted)
  const getDeletedRowStyle = (row: Cliente) => {
    if (row.isActive === false) {
      return {
        opacity: 0.6,
        fontStyle: 'italic',
        backgroundColor: 'action.hover',
        '& td': {
          color: 'text.disabled',
          fontStyle: 'italic'
        }
      };
    }
    return {};
  };

  const getDeletedCardStyle = (row: Cliente) => {
    if (row.isActive === false) {
      return {
        opacity: 0.7,
        backgroundColor: 'action.disabledBackground',
        '& *': {
          fontStyle: 'italic'
        }
      };
    }
    return {};
  };

  const renderActions = (row: Cliente) => {
    const isDeleted = row.isActive === false;

    if (isDeleted) {
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Ripristina">
            <IconButton
              size="small"
              color="success"
              onClick={() => restoreMutation.mutate(row.id)}
              disabled={restoreMutation.isPending || hardDeleteMutation.isPending}
            >
              <RestoreFromTrash fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Elimina definitivamente">
            <IconButton
              size="small"
              color="error"
              onClick={() => setHardDeleteDialog({
                open: true,
                id: row.id,
                nome: `${row.cognome} ${row.nome}`.trim()
              })}
              disabled={restoreMutation.isPending || hardDeleteMutation.isPending}
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="Visualizza">
          <IconButton size="small" onClick={() => navigate(`/clienti/${row.id}`)}>
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Modifica">
          <IconButton size="small" onClick={() => navigate(`/clienti/${row.id}/edit`)}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Elimina">
          <IconButton
            size="small"
            color="error"
            onClick={() => setDeleteDialog({ open: true, id: row.id })}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  if (isLoading && !data) {
    return (
      <AppLayout>
        <LoadingSpinner message="Caricamento clienti..." />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box>
        <Box sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2
        }}>
          <Typography variant="h4" component="h1" fontWeight={600}>
            Gestione Clienti
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/clienti/new')}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Nuovo Cliente
          </Button>
        </Box>

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' } }}>
            <form onSubmit={(e) => e.preventDefault()} style={{ flex: 1 }}>
              <TextField
                label="Cerca"
                placeholder="Nome, cognome, codice fiscale..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                size="small"
                fullWidth
                helperText={search !== debouncedSearch ? "Ricerca in corso..." : undefined}
              />
            </form>
            <FormControlLabel
              control={
                <Switch
                  checked={showDeleted}
                  onChange={(e) => {
                    setShowDeleted(e.target.checked);
                    setPage(0);
                  }}
                  color="warning"
                />
              }
              label="Mostra eliminati"
              sx={{ whiteSpace: 'nowrap' }}
            />
          </Box>
        </Paper>

        <DataTable
          columns={columns}
          data={data?.data || []}
          total={data?.total || 0}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          actions={renderActions}
          loading={isLoading}
          getRowSx={getDeletedRowStyle}
          getCardSx={getDeletedCardStyle}
          emptyStateComponent={
            <EmptyState
              title="Nessun cliente trovato"
              message={
                search
                  ? "Non ci sono clienti che corrispondono alla tua ricerca. Prova con un termine diverso o verifica l'ortografia."
                  : "Non hai ancora aggiunto nessun cliente. Inizia creando il tuo primo cliente per gestire le scadenze."
              }
              actionLabel={!search ? "Aggiungi Primo Cliente" : undefined}
              onAction={!search ? () => navigate('/clienti/new') : undefined}
            />
          }
        />

        <ConfirmDialog
          open={deleteDialog.open}
          title="Conferma Eliminazione"
          message="Sei sicuro di voler eliminare questo cliente? Questa azione non puo essere annullata."
          confirmText="Elimina"
          confirmColor="error"
          onConfirm={() => deleteDialog.id && deleteMutation.mutate(deleteDialog.id)}
          onCancel={() => setDeleteDialog({ open: false, id: null })}
          loading={deleteMutation.isPending}
        />

        <ConfirmDialog
          open={hardDeleteDialog.open}
          title="Eliminazione Definitiva"
          message={`Sei sicuro di voler eliminare DEFINITIVAMENTE il cliente "${hardDeleteDialog.nome}"? Questa azione e' IRREVERSIBILE e il cliente sara' rimosso permanentemente dal database.`}
          confirmText="Si, conferma"
          cancelText="Annulla"
          confirmColor="error"
          onConfirm={() => hardDeleteDialog.id && hardDeleteMutation.mutate(hardDeleteDialog.id)}
          onCancel={() => setHardDeleteDialog({ open: false, id: null, nome: '' })}
          loading={hardDeleteMutation.isPending}
        />
      </Box>
    </AppLayout>
  );
};

export default ClientiListPage;
